<script setup>
import { onBeforeMount } from 'vue';
import BreezeButton from '@/Components/Button.vue';
import BreezeCheckbox from '@/Components/Checkbox.vue';
import BreezeGuestLayout from '@/Layouts/Guest.vue';
import BreezeInput from '@/Components/Input.vue';
import BreezeLabel from '@/Components/Label.vue';
import BreezeValidationErrors from '@/Components/ValidationErrors.vue';
import { Head, Link, useForm } from '@inertiajs/vue3';
import { useCrossFrameMessaging } from '@apparatix/composables';
import { ref } from 'vue';

defineProps({
  canResetPassword: Boolean,
  status: String,
  companyLogo: String,
});

const form = useForm({
  user_userid: '',
  password: '',
  remember: false,
});

const isCapsLockOn = ref(false);

const checkCapsLock = async (event) => {
    // Special check for if capslock is already on and toggled off the modifier state is still capslock till the next key press
    if (isCapsLockOn.value && event.code === 'CapsLock') {
        isCapsLockOn.value = false;
        return;
    }

    isCapsLockOn.value = event.getModifierState('CapsLock');
};

const { sendMessage } = useCrossFrameMessaging();

const submit = () => {
  form.post(route('login'), {
    onFinish: () => form.reset('password'),
  });
};

// This has no effect in the legacy design, or on the main login page for either design. In the new design, when a user's session expires due to inactivity, the iframed page
// redirects to the login page, but leaves the parent app container in place. This function lets the parent frame (if present) know that the user has been logged out so that it can
// respond accordingly
onBeforeMount(() => sendMessage({ type: 'login-page-opened' }));

</script>

<template>
  <BreezeGuestLayout :company-logo="companyLogo">
    <Head title="Log in" />

    <BreezeValidationErrors class="mb-4" />

    <div v-if="status" class="mb-4 font-medium text-sm text-green-600">
      {{ status }}
    </div>

    <form @submit.prevent="submit">
      <div>
        <BreezeLabel for="user_userid" value="User ID" />
        <BreezeInput
          id="user_userid"
          v-model="form.user_userid"
          type="text"
          class="mt-1 block w-full"
          required
          autofocus
          autocomplete="username"
        />
      </div>

      <div class="mt-4">
        <BreezeLabel for="password" value="Password" />
        <BreezeInput
          id="password"
          v-model="form.password"
          type="password"
          class="mt-1 block w-full"
          required
          autocomplete="current-password"
          @keyup="checkCapsLock"
        />
          <div v-if="isCapsLockOn" class="text-red-500 text-sm mt-1">
              Warning: Caps Lock is on!
          </div>
      </div>

      <div class="block mt-4">
        <label class="flex items-center">
          <BreezeCheckbox v-model:checked="form.remember" name="remember" />
          <span class="ml-2 text-sm text-gray-600">Keep me logged in</span>
        </label>
      </div>

      <div class="flex items-center justify-end mt-4">
        <Link v-if="canResetPassword" :href="route('password.request')" class="underline text-sm text-gray-600 hover:text-gray-900">
          Forgot your password?
        </Link>

        <BreezeButton class="ml-4" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
          Log in
        </BreezeButton>
      </div>
    </form>
  </BreezeGuestLayout>
</template>
